import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/nationals.awards.ts'

type QuickLinkSection = {
	id: string
	label: string
	cards: Array<{ id: string; label: string }>
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
	{ id: 'adjudication-awards', label: 'Adjudication Awards', cards: [] },
	{ id: 'solo-awards', label: 'Solo Overall Awards', cards: [] },
	{
		id: 'duotrio-awards',
		label: 'Duo/Trio Overall Awards',
		cards: [
			{ id: 'duotrio-style-runner-up', label: 'Style Runner Up' },
			{ id: 'duotrio-style-high-score', label: 'Style High Score' },
			{ id: 'duotrio-judges-pick', label: "Judges' Pick" },
			{ id: 'duotrio-directors-pick', label: "Directors' Pick" },
			{ id: 'duotrio-2nd-5th', label: '2nd – 5th Place' },
			{ id: 'duotrio-age-high-score', label: 'Age Division High Score' },
			{ id: 'duotrio-danceagain', label: 'DanceAgain' },
			{
				id: 'duotrio-performance-of-the-year',
				label: 'Duo/Trio Performance of the Year',
			},
		],
	},
	{
		id: 'group-awards',
		label: 'Group Overall Awards',
		cards: [
			{ id: 'group-style-runner-up', label: 'Style Runner Up' },
			{ id: 'group-style-high-score', label: 'Style High Score' },
			{ id: 'group-judges-pick', label: "Judges' Pick" },
			{ id: 'group-directors-pick', label: "Directors' Pick" },
			{ id: 'group-2nd-5th', label: '2nd – 5th Place in Category' },
			{ id: 'group-category-high-score', label: 'Category High Score' },
			{ id: 'group-age-high-score', label: 'Age Division High Score' },
			{ id: 'group-danceagain', label: 'DanceAgain' },
			{
				id: 'group-performance-of-the-year',
				label: 'Performance of the Year',
			},
		],
	},
	{
		id: 'studio-awards',
		label: 'Studio Awards',
		cards: [
			{ id: 'studio-shaffer', label: 'The Shaffer Studio' },
			{ id: 'studio-best-choreography', label: 'Best Studio Choreography' },
			{ id: 'studio-most-technical', label: 'Most Technical Studio' },
			{ id: 'studio-standout', label: 'The Standout Studio' },
			{ id: 'studio-breakthrough', label: 'The Breakthrough Studio' },
			{ id: 'studio-of-the-year', label: 'Studio of the Year' },
		],
	},
	{ id: 'questions', label: 'Questions', cards: [] },
]

function QuickLinksPanel() {
	return (
		<div className="border border-muted-foreground/30 bg-muted/30 p-4 sm:p-6">
			<div className="mb-4 flex items-center gap-3 sm:mb-5">
				<div className="flex h-8 w-8 items-center justify-center text-primary">
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
						/>
					</svg>
				</div>
				<h3 className="font-display text-base font-bold tracking-wide sm:text-lg">
					QUICK LINKS
				</h3>
			</div>

			<div className="space-y-3">
				{QUICK_LINK_SECTIONS.map((section, sectionIndex) => (
					<div
						key={section.id}
						className="border border-border/50 bg-background/95 p-3"
					>
						<div className="mb-2 flex items-center gap-2">
							<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-foreground">
								{sectionIndex + 1}
							</span>
							<a
								href={`#${section.id}`}
								className="inline-flex flex-1 items-center border border-border/60 bg-muted/40 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:border-primary/50 hover:text-primary"
							>
								<span>{section.label}</span>
							</a>
						</div>

						{section.cards.length > 0 ? (
							<div className="ml-2 space-y-1.5 border-l border-border/50 pl-3">
								{section.cards.map((card) => (
									<a
										key={card.id}
										href={`#${card.id}`}
										className="group flex items-start gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs leading-snug text-foreground/85 transition-all hover:border-border/60 hover:bg-muted/40 hover:text-foreground"
									>
										<span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 transition-colors group-hover:bg-primary" />
										<span className="flex-1">{card.label}</span>
									</a>
								))}
							</div>
						) : (
							<p className="ml-2 text-xs text-muted-foreground">
								General section details.
							</p>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Nationals Awards | DanceMakers' },
	{
		name: 'description',
		content:
			'Learn about DanceMakers Nationals competition awards including adjudication awards, solo awards, duo/trio awards, group awards, Performance of the Year, and studio recognition.',
	},
]

export default function NationalsAwardsRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Nationals Awards
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						DanceMakers recognizes excellence through a comprehensive awards
						system at Nationals. From adjudication scores to Performance of the
						Year with cash prizes, we celebrate outstanding performances across
						all categories and age divisions.
					</p>
				</div>
			</section>

			{/* Desktop-only floating quick links */}
			<div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8 lg:px-6 xl:px-12">
				<div className="min-w-0">
					{/* Adjudication Awards */}
					<section
						id="adjudication-awards"
						className="scroll-mt-36 bg-muted/30 py-8 md:py-12"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Adjudication Awards
							</h2>

							<div className="mb-6 max-w-4xl space-y-3 text-xs leading-relaxed text-muted-foreground sm:mb-8 sm:space-y-4 sm:text-sm">
								<p>
									<span className="font-medium text-foreground">
										All routines will be adjudicated by three of the DanceMakers
										judges.
									</span>
								</p>
								<p>
									Solo routines will receive an adjudication score in your
									registration portal, but will not be announced for an
									adjudication award.{' '}
									<span className="font-medium text-foreground">
										All soloist will receive a participation award.
									</span>
								</p>
								<p>
									Duo/Trio and Group routines will receive an adjudication score
									in your registration portal and will be announced for an
									adjudication during competition award ceremony.
								</p>
								<p>
									If a routine performing was choreographed by one of the judges,
									the final score is an average of the other two judges.{' '}
									<span className="font-medium text-foreground">
										The choreographer does not score the routine.
									</span>
								</p>
							</div>

							<div className="-mx-6 max-w-2xl overflow-x-auto px-6 md:mx-0 md:px-0">
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Adjudication
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Points
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												High 1st Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												96-100
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												1st Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												90-95
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												High 2nd Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												85-89
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												2nd Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												80-84
											</td>
										</tr>
										<tr>
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												3rd Place
											</td>
											<td className="p-2 font-medium sm:p-4">70-79</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</section>

					{/* Solo Overall Awards */}
					<section id="solo-awards" className="scroll-mt-36 py-10 md:py-16">
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Solo Overall Awards
							</h2>

							<div className="-mx-6 mb-6 max-w-4xl overflow-x-auto px-6 sm:mb-8 md:mx-0 md:px-0">
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Awards
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Prize
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												High Score
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												Acrylic Award / Scholarship to 2027-28 Tour / Solo High
												Score Performance
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												1st Runner Up
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												Acrylic Award / Two city Regional Scholarship to 2027-28
												Tour
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												2nd Runner Up
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												Acrylic Award / Two city Regional Scholarship to 2027-28
												Tour
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												3rd Runner Up
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												Acrylic Award / Two city Regional Scholarship to 2027-28
												Tour
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												4th Runner Up
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												Acrylic Award / Two city Regional Scholarship to 2027-28
												Tour
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												5th-19th Runner Up
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												Acrylic Award
											</td>
										</tr>
										<tr>
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												20th-29th Runner Up
											</td>
											<td className="p-2 sm:p-4">
												Announced at Solo Awards Ceremony
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div className="max-w-4xl space-y-3 text-xs leading-relaxed text-muted-foreground sm:text-sm">
								<p>
									<span className="font-medium text-foreground">
										Select Solo Winners must be available to perform in the Wrap
										It Ceremony and all daily rehearsals.
									</span>{' '}
									The performance will include Camp Dance High Score, Junior High
									Score through 4th Runner Up, Pre-Teen High Score through 9th
									Runner Up, Teen High Score through 29th Runner Up, and Senior
									High Score through 29th Runner Up.
								</p>
								<p>
									<span className="font-medium text-foreground">
										Soloists cannot win consecutive titles in the same age
										division.
									</span>
								</p>
								<p>
									<span className="font-medium text-primary">
										New this year:
									</span>
									Dancers are only able to compete one solo at Nationals. If
									dancers qualified more than one solo regionally, they may only
									register one for Nationals.
								</p>
							</div>
						</div>
					</section>

					{/* Duo/Trio Overall Awards */}
					<section
						id="duotrio-awards"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Duo/Trio Overall Awards
							</h2>

							<div className="mb-4 grid max-w-6xl gap-4 sm:mb-6 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="duotrio-style-runner-up" className="scroll-mt-36">
									<InfoCard title="Style Runner Up">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Recognition
											</span>
										</p>
										<p className="mb-3">
											Duo/Trio Runner Up in each age division (Camp Dance,
											Junior, Pre-Teen, Teen or Senior) and style (Ballet, Tap,
											Jazz, Lyrical, Contemporary, Musical Theatre, Hip Hop or
											Open).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Tap Duo/Trio Runner Up"
											</span>
										</p>
										<p className="text-xs">
											Announced on our website later in the week.
										</p>
									</InfoCard>
								</div>

								<div id="duotrio-style-high-score" className="scroll-mt-36">
									<InfoCard title="Style High Score">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Recognition
											</span>
										</p>
										<p className="mb-3">
											Duo/Trio High Score in each age division (Camp Dance,
											Junior, Pre-Teen, Teen or Senior) and style (Ballet, Tap,
											Jazz, Lyrical, Contemporary, Musical Theatre, Hip Hop or
											Open).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Tap Duo/Trio High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced on our website later in the week.
										</p>
									</InfoCard>
								</div>

								<div id="duotrio-judges-pick" className="scroll-mt-36">
									<InfoCard title="Judges' Pick">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award
											</span>
										</p>
										<p className="mb-3">
											Routines the judges pick for special recognition in each
											age division nondependent on scores.
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="duotrio-directors-pick" className="scroll-mt-36">
									<InfoCard title="Directors' Pick">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award
											</span>
										</p>
										<p className="mb-3">
											Routines that the directors pick for special recognition in
											each age division nondependent on scores.
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="duotrio-2nd-5th" className="scroll-mt-36">
									<InfoCard title="2nd – 5th Place">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award
											</span>
										</p>
										<p className="mb-3">
											2nd – 5th in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Duo/Trio 2nd"
											</span>
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="duotrio-age-high-score" className="scroll-mt-36">
									<InfoCard title="Age Division High Score">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award / $500
											</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior) excluding Category High Scores.
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Duo/Trio High Score"
											</span>
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div
									id="duotrio-danceagain"
									className="scroll-mt-36 col-span-3"
								>
									<InfoCard title="DanceAgain">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Performs at DanceAgain Showcase
											</span>
										</p>
										<p>
										The National DanceAgain Showcase will feature a selection of the TOP-scoring routines from across all five age divisions in both duo/trio competition and group competition (Group, Line, Extended Line, and Production) to determine the Duo/Trio Performance of the Year and Group Performance of the Year. While routines may be selected from any age division, representation from every age division/category is not guaranteed. Studios may have multiple routines selected for the Showcase across different age divisions/categories; however, only one routine per studio may be represented within the same age division/categories. All judges from the week will view the Showcase performances and select the two final winners based solely on their performance in the Showcase, not on their previous competition scores. All other awards will be determined by the scores earned during the competition.
										</p>
									</InfoCard>
								</div>

								<div
									id="duotrio-performance-of-the-year"
									className="scroll-mt-36 col-span-3"
								>
									<InfoCard title="Duo/Trio Performance of the Year">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award / $1,000 CASH
											</span>
										</p>
										<p className="mb-3">
											This award is chosen by our judging panel from the
											DanceAgain Showcase which consists of the TOP scoring
											performances from our Nationals Competition.
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>
							</div>

							<p className="max-w-4xl text-xs italic text-muted-foreground">
								* DanceMakers reserves the rights to extend placement awards
								depending on the number of registered routines.
							</p>
						</div>
					</section>

					{/* Group Overall Awards */}
					<section id="group-awards" className="scroll-mt-36 py-10 md:py-16">
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Group Overall Awards
							</h2>

							<div className="mb-4 grid max-w-6xl gap-4 sm:mb-6 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="group-style-runner-up" className="scroll-mt-36">
									<InfoCard title="Style Runner Up">
										<p className="mb-3">
											<span className="font-medium text-primary">Plaque</span>
										</p>
										<p className="mb-3">
											Runner Up in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior), category (Group, Line, Extended
											Line or Production) and style (Ballet, Tap, Jazz, Lyrical,
											Contemporary, Musical Theatre, Hip Hop or Open).
										</p>
										<p className="mb-3">
											<span className="font-medium text-primary">
												Example: "Junior Tap Group Runner Up"
											</span>
										</p>
										<p className="text-xs">
											Announced on our website later in the week.
										</p>
									</InfoCard>
								</div>

								<div id="group-style-high-score" className="scroll-mt-36">
									<InfoCard title="Style High Score">
										<p className="mb-3">
											<span className="font-medium text-primary">Plaque</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior), category (Group, Line, Extended
											Line or Production) and style (Ballet, Tap, Jazz, Lyrical,
											Contemporary, Musical Theatre, Hip Hop or Open).
										</p>
										<p className="mb-3">
											<span className="font-medium text-primary">
												Example: "Junior Tap Group High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced on our website later in the week.
										</p>
									</InfoCard>
								</div>

								<div id="group-judges-pick" className="scroll-mt-36">
									<InfoCard title="Judges' Pick">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award
											</span>
										</p>
										<p className="mb-3">
											Routines the judges pick for special recognition in each
											age division and category nondependent on scores.
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="group-directors-pick" className="scroll-mt-36">
									<InfoCard title="Directors' Pick">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award
											</span>
										</p>
										<p className="mb-3">
											Routines that the directors pick for special recognition in
											each age division and category nondependent on scores.
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="group-2nd-5th" className="scroll-mt-36">
									<InfoCard title="2nd – 5th Place in Category">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award
											</span>
										</p>
										<p className="mb-3">
											2nd – 5th in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior) and category (Group, Line,
											Extended Line or Production).
										</p>
										<p className="mb-3">
											<span className="font-medium text-primary">
												Example: "Junior Group 2nd Place"
											</span>
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="group-category-high-score" className="scroll-mt-36">
									<InfoCard title="Category High Score">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award / $500
											</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior) and category (Group, Line,
											Extended Line or Production).
										</p>
										<p className="mb-3">
											<span className="font-medium text-primary">
												Example: "Junior Group High Score"
											</span>
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="group-age-high-score" className="scroll-mt-36">
									<InfoCard title="Age Division High Score">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award / $1,000
											</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior,
											Pre-Teen, Teen or Senior) excluding Category High Scores.
										</p>
										<p className="mb-3">
											<span className="font-medium text-primary">
												Example: "Junior High Score"
											</span>
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>

								<div id="group-danceagain" className="scroll-mt-36 col-span-2">
									<InfoCard title="DanceAgain">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Performs at DanceAgain Showcase
											</span>
										</p>
										<p>
										The National DanceAgain Showcase will feature a selection of the TOP-scoring routines from across all five age divisions in both duo/trio competition and group competition (Group, Line, Extended Line, and Production) to determine the Duo/Trio Performance of the Year and Group Performance of the Year. While routines may be selected from any age division, representation from every age division/category is not guaranteed. Studios may have multiple routines selected for the Showcase across different age divisions/categories; however, only one routine per studio may be represented within the same age division/categories. All judges from the week will view the Showcase performances and select the two final winners based solely on their performance in the Showcase, not on their previous competition scores. All other awards will be determined by the scores earned during the competition.
										
										</p>
									</InfoCard>
								</div>

								<div
									id="group-performance-of-the-year"
									className="scroll-mt-36 col-span-3"
								>
									<InfoCard title="Performance of the Year">
										<p className="mb-3">
											<span className="font-medium text-primary">
												Acrylic Award / $2,000 CASH
											</span>
										</p>
										<p className="mb-3">
											This award is chosen by our judging panel from the
											DanceAgain Showcase which consists of the TOP scoring
											performances from our Nationals Competition.
										</p>
										<p className="text-xs">Announced at the Wrap It Ceremony.</p>
									</InfoCard>
								</div>
							</div>

							<p className="max-w-4xl text-xs italic text-muted-foreground">
								* DanceMakers reserves the rights to extend placement awards
								depending on the number of registered routines.
							</p>
						</div>
					</section>

					{/* Studio Awards */}
					<section
						id="studio-awards"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Studio Awards
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div
									id="studio-shaffer"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-6"
								>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-foreground sm:mb-3 sm:text-base">
										The Shaffer Studio
									</h3>
									<p className="mb-2 text-xs font-medium text-primary sm:mb-3">
										Acrylic Award / $500
									</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										This will be awarded to the studio that shows{' '}
										<span className="font-medium text-foreground">
											support, encouragement, professionalism and shares their
											love for dance with others
										</span>{' '}
										throughout the event. This award is in honor of Mr. Doug
										Shaffer who has shared his love of dance with others for many
										years.
									</p>
								</div>

								<div
									id="studio-best-choreography"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-6"
								>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-foreground sm:mb-3 sm:text-base">
										Best Studio Choreography
									</h3>
									<p className="mb-2 text-xs font-medium text-primary sm:mb-3">
										Acrylic Award / $500
									</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										This will be awarded to the studio that has the{' '}
										<span className="font-medium text-foreground">
											best overall choreography
										</span>{' '}
										throughout competition.
									</p>
								</div>

								<div
									id="studio-most-technical"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-6"
								>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-foreground sm:mb-3 sm:text-base">
										Most Technical Studio
									</h3>
									<p className="mb-2 text-xs font-medium text-primary sm:mb-3">
										Acrylic Award / $500
									</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										This will be awarded to the studio that has the{' '}
										<span className="font-medium text-foreground">
											best overall technique
										</span>{' '}
										throughout convention and competition.
									</p>
								</div>

								<div
									id="studio-standout"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-6"
								>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-foreground sm:mb-3 sm:text-base">
										The Standout Studio
									</h3>
									<p className="mb-2 text-xs font-medium text-primary sm:mb-3">
										Acrylic Award / $500
									</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										This will be awarded to the studio that{' '}
										<span className="font-medium text-foreground">
											stands out in convention work and competition performance
										</span>
										.
									</p>
								</div>

								<div
									id="studio-breakthrough"
									className="scroll-mt-36 border border-muted-foreground/30 p-4 sm:p-6"
								>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-foreground sm:mb-3 sm:text-base">
										The Breakthrough Studio
									</h3>
									<p className="mb-2 text-xs font-medium text-primary sm:mb-3">
										Acrylic Award / $500
									</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										This will be awarded to the studio that has the{' '}
										<span className="font-medium text-foreground">
											most overall improvement from Regionals to Nationals
										</span>
										.
									</p>
								</div>

								<div
									id="studio-of-the-year"
									className="scroll-mt-36 border border-muted-foreground/30 bg-accent/5 p-4 sm:p-6"
								>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-foreground sm:mb-3 sm:text-base">
										Studio of the Year
									</h3>
									<p className="mb-2 text-xs font-medium text-primary sm:mb-3">
										Acrylic Award / $1,000
									</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Studio of the Year is awarded to the studio who{' '}
										<span className="font-medium text-foreground">
											embodies greatness in all aspects at Nationals
										</span>
										. This includes, but is not limited to, competition,
										convention and the in-between moments throughout the event.
										This studio is to represent the highest caliber in dance,
										along with being supportive and encouraging to other dancers.
										The Studio of the Year is to represent DanceMakers values and
										mission and is meant to inspire other studios throughout the
										country.
									</p>
								</div>
							</div>
						</div>
					</section>

					{/* Contact CTA */}
					<section
						id="questions"
						className="relative z-50 scroll-mt-36 bg-background py-10 md:py-16"
					>
						<div className="container px-6 text-center md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
								Questions About Awards?
							</h2>
							<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
								Have questions about our Nationals awards system or recognition
								programs? Our team is here to help!
							</p>
							<a
								href="/contact"
								className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
							>
								Contact Us
							</a>
						</div>
					</section>
				</div>

				<aside className="hidden lg:sticky lg:top-32 lg:block lg:self-start">
					<div className="relative">
						<div className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
							<QuickLinksPanel />
						</div>
					</div>
				</aside>
			</div>
		</div>
	)
}
